import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import type React from "react";
import { fmtMoneyWhole } from "../ads";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import { StatusBadge } from "./Ui";
import { DataTable, RequestView, useMutation, type RecordData } from "./Workflow";

type OverviewClient = {
  id: number;
  name: string;
  tracking_tier: string | null;
  ads_accounts: number;
  api_actions: number;
  sheet_actions: number;
  last_sent_at: string | null;
  failed: number;
  pending: number;
  /** Google's last refusal of this client's uploads (e.g. the Data Manager API is off), until one goes through. */
  send_error?: string | null;
  spend_30d: number;
  booked_30d: number;
  revenue_30d: number;
  credited_30d: number;
  last_upload: string | null;
  roas_30d: number | null;
  cost_per_booked_30d: number | null;
  /** Where revenue_30d came from: the job export, or GHL opportunities marked won (only when there are no jobs). */
  revenue_source: "jobs" | "ghl_won" | null;
  won_deals_30d: number | null;
};
type Overview = {
  clients: OverviewClient[];
  schedules: Record<string, string>;
  syncs_on: boolean;
  google_scope_ok: boolean;
};
/** GET /api/connections/health: each feed's last sync, agency-wide. */
type FeedHealth = Partial<Record<"google_ads" | "ctm" | "ghl", { status: string; label?: string; last_run_at?: string | null }>>;
const FEED_SHORT: Record<string, string> = { google_ads: "Google Ads", ctm: "CTM", ghl: "GHL" };
const FEED_DOWN = new Set(["failing", "needs_key", "stale"]);

// The nightly syncs cron runs at 10:00 UTC (vercel.json): 6 AM Eastern in summer, 5 AM in winter.
const SYNC_TIME = (() => {
  const run = new Date();
  run.setUTCHours(10, 0, 0, 0);
  return `${run.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} Eastern`;
})();

/** Google's refusal in plain words. The Data Manager API switched off in the Cloud project is the known case. */
export function googleRefusal(message: string): { text: string; url: string | null } {
  const project = /project (\d{6,})/.exec(message)?.[1];
  const url = /https:\/\/console\.(?:developers|cloud)\.google\.com\/[^\s]+?(?=\s|$)/.exec(message)?.[0]?.replace(/[.,)]+$/, "") ?? null;
  if (/has not been used in project|is disabled/i.test(message))
    return { text: `The Data Manager API is switched off in the Google Cloud project behind NOLA's Google login${project ? ` (project ${project})` : ""}. Once it is on, the waiting conversions go out on their own within minutes.`, url };
  return { text: `Google says: ${message.replace(/^Data Manager \d+:\s*/, "")}`, url };
}

const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
const ago = (iso: string | null) => {
  if (!iso) return "Never";
  const d = daysSince(iso);
  return d <= 0 ? "Today" : d === 1 ? "Yesterday" : `${d} days ago`;
};
/** How long a feed has gone without refreshing: "in 27 days", never "since 27 days ago". */
const quietFor = (iso: string) => {
  const d = daysSince(iso);
  return d <= 0 ? " today" : d === 1 ? " since yesterday" : ` in ${d} days`;
};

/**
 * Every active client's tracking at a glance, for the agency: whether its
 * conversions reach Google Ads, what is stuck, and what the last 30 days cost
 * and returned. The two switches the whole machine depends on sit at the top.
 */
export default function TrackingOverview() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const overview = useApi<Overview>("/api/tracking/overview");
  // Which feeds' last nightly run failed: "On" alone read as healthy while Settings said Failed.
  const feeds = useApi<FeedHealth>("/api/connections/health");
  const mutation = useMutation();
  const o = overview.data;
  const down = Object.entries(feeds.data || {}).filter(([, f]) => f && FEED_DOWN.has(f.status)) as [string, { status: string; last_run_at?: string | null }][];
  const failing = down.map(([k]) => FEED_SHORT[k] || k);
  // Say what is wrong with each feed: a feed that needs a key never ran, it did not "fail on the last run".
  const feedProblem = ([k, f]: [string, { status: string; last_run_at?: string | null }]) => {
    const name = FEED_SHORT[k] || k;
    if (f.status === "needs_key") return `${name} needs a new key${f.last_run_at ? ` (last ran ${ago(f.last_run_at).toLowerCase()})` : ""}`;
    if (f.status === "stale") return `${name} has not refreshed${f.last_run_at ? quietFor(f.last_run_at) : ""}`;
    return `${name} failed on the last run`;
  };
  const anyFailed = down.some(([, f]) => f.status === "failing");

  const turnOn = async () => {
    if (!window.confirm("This deployment will run the nightly Google Ads, GoHighLevel and CTM syncs. Only do this on the one deployment that owns them.")) return;
    const r = await mutation.run<{ turned_on: string[] }>("/api/operations/schedules/turn-on", {});
    if (r) {
      mutation.setNotice(r.turned_on.length ? `Nightly syncs turned on: ${r.turned_on.join(", ")}. The first run is tomorrow at ${SYNC_TIME}.` : "Nightly syncs were already on.");
      overview.reload();
    }
  };

  const rows: RecordData[] = (o?.clients || []).map((c) => {
    // No job export and no won deals in the window means revenue is unknown, not zero.
    // An export from months ago doesn't make this window's revenue zero: only a
    // revenue source inside the window counts.
    const hasOutcomes = c.revenue_source != null;
    const accounts = c.ads_accounts > 1 ? <small className="tracking-accounts">{c.ads_accounts} accounts</small> : null;
    // One wrapper, so the "3 accounts" line stays under the badge in phone cards.
    const conversionCell = (badge: React.ReactNode) => <span className="tracking-conversion">{badge}{accounts}</span>;
    return {
      id: c.id,
      client: <Link className="tracking-client" to={`/tracking?client_id=${c.id}`}>{c.name}</Link>,
      // How this client sends conversions (Direct, Sheet, Not connected): a way, not a count.
      sending: c.api_actions > 0
        ? conversionCell(<StatusBadge tone="success">Direct</StatusBadge>)
        : c.sheet_actions > 0
          ? conversionCell(<StatusBadge tone="info">Sheet</StatusBadge>)
          : c.ads_accounts > 0
            ? conversionCell(<StatusBadge>Not connected</StatusBadge>)
            : <span className="muted">No Google Ads</span>,
      queue: c.send_error ? <span title={c.send_error}><StatusBadge tone="danger">{`Blocked, ${c.pending} waiting`}</StatusBadge></span>
        : c.failed ? <StatusBadge tone="danger">{`${c.failed} failed`}</StatusBadge> : c.pending ? `${c.pending} pending` : c.api_actions ? "Clear" : null,
      last_send: c.api_actions ? ago(c.last_sent_at) : null,
      last_export: ago(c.last_upload),
      // Whole dollars, as on Ads ($919, not $919.27); the note says "last 30 days".
      // No Google Ads account: no spend to show (a dash, like the rest of its row).
      spend: c.ads_accounts > 0 ? <>{fmtMoneyWhole(c.spend_30d)}</> : null,
      booked_jobs: hasOutcomes ? c.booked_30d : null,
      // Won deal value is not invoiced work: say so in the cell, so nobody reads it as jobs.
      revenue: c.revenue_source === "ghl_won"
        ? <span title={`Revenue: GHL won deals (${c.won_deals_30d ?? 0}), not invoiced jobs`}>{fmtMoneyWhole(c.revenue_30d)}<em className="tracking-tag">GHL won deals</em></span>
        : hasOutcomes ? <>{fmtMoneyWhole(c.revenue_30d)}</> : null,
      roas: c.roas_30d == null || !hasOutcomes ? null : `${c.roas_30d.toFixed(1)}×`,
      // Bookings Google Ads counted from what we sent (offline conversion imports).
      offline_conversions_credited: c.ads_accounts > 0 ? c.credited_30d : null,
    };
  });
  const connected = (o?.clients || []).filter((c) => c.api_actions > 0).length;
  const withAds = (o?.clients || []).filter((c) => c.ads_accounts > 0).length;
  const failed = (o?.clients || []).reduce((n, c) => n + c.failed, 0);
  const blocked = (o?.clients || []).filter((c) => c.send_error);
  const blockedNote = blocked.length ? googleRefusal(blocked[0].send_error || "") : null;

  return (
    <div className="workflow-stack">
      {mutation.feedback}
      <section className="panel tracking-overview">
        <div className="panel-heading">
          <div>
            <h2>Conversion sync</h2>
          </div>
          <div className="feed-actions-row">
            {isAdmin && !o?.syncs_on && <button className="button" disabled={mutation.busy || !o} onClick={() => void turnOn()}>Turn on nightly syncs</button>}
            <button className="button secondary" disabled={overview.refreshing} onClick={() => { overview.reload(); feeds.reload(); }}><RefreshCw className={overview.refreshing ? "spin" : ""} aria-hidden="true" />{overview.refreshing ? "Refreshing…" : "Refresh"}</button>
          </div>
        </div>
        {blocked.length > 0 && (
          <p className="inline-notice danger" role="alert"><span>
            {`Google is refusing conversion uploads for ${blocked.map((c) => c.name).join(", ")}, so ${blocked.reduce((n, c) => n + c.pending, 0).toLocaleString()} wait to be sent. `}
            {blockedNote?.text}{" "}
            {blockedNote?.url && <a href={blockedNote.url} target="_blank" rel="noreferrer">Turn it on in Google Cloud</a>}
          </span></p>
        )}
        <RequestView request={overview}>
          <dl className="feed-status feed-status-4">
            <div>
              <dt>Google connection</dt>
              <dd><StatusBadge tone={o?.google_scope_ok && !blocked.length ? "success" : "danger"}>{!o?.google_scope_ok ? "Not connected" : blocked.length ? "Blocked by Google" : "Ready"}</StatusBadge></dd>
              {o?.google_scope_ok
                ? <span>{blocked.length ? "The permission is on the Google token, but Google refuses the uploads (see above)." : "The Data Manager permission is on the stored Google token."}</span>
                : <span className="feed-status-action">Allow Data Manager when you reconnect, or nothing can be sent.{isAdmin && <Link className="button secondary" to="/settings#integrations/google_workspace">Reconnect Google</Link>}</span>}
            </div>
            <div>
              <dt>Nightly syncs</dt>
              <dd><StatusBadge tone={!o?.syncs_on ? "warning" : failing.length ? "warning" : "success"}>{!o?.syncs_on ? "Off" : anyFailed ? "On, last run failed" : failing.length ? "On, needs attention" : "On"}</StatusBadge></dd>
              <span>{!o?.syncs_on
                ? "Nothing refreshes on its own until this is on. Uploads still send conversions at once."
                : failing.length
                  ? <>{`${down.map(feedProblem).join(". ")}. `}{isAdmin ? <Link to="/settings#security">See System &amp; security</Link> : "An administrator can see why in Settings."}</>
                  : `Google Ads spend, credited counts, GHL and CTM refresh every morning at ${SYNC_TIME}.`}</span>
            </div>
            <div>
              <dt>Sending conversions to Google</dt>
              <dd><strong>{o ? `${connected} of ${withAds}` : "—"}</strong></dd>
              <span>Clients with Google Ads whose conversions go direct</span>
            </div>
            <div>
              <dt>Stuck</dt>
              <dd><strong>{o ? failed.toLocaleString() : "—"}</strong></dd>
              <span>{failed ? "Failed conversions. Open the client to see why." : "No failed conversions"}</span>
            </div>
          </dl>
          <DataTable
            rows={rows}
            columns={["client", "sending", "queue", "last_send", "last_export", "spend", "booked_jobs", "revenue", "roas", "offline_conversions_credited"]}
            empty="No active clients."
          />
          <p className="workflow-note tracking-table-note">Last 30 days. A dash means there is nothing to measure yet. Help explains each column.</p>
        </RequestView>
      </section>
    </div>
  );
}
